import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  EyeOff,
  FoldHorizontal,
  FoldVertical,
  GripVertical,
  MoveDiagonal2,
  MoveHorizontal,
  UnfoldHorizontal,
  UnfoldVertical
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ITEM_KINDS } from '../../config/sectionLayouts';
import { cellStyle, GRID_CELL_CLASS } from './gridCell';

const MOVE_STEPS = [
  { value: 'left', label: 'Сдвинуть влево', Icon: ArrowLeft },
  { value: 'right', label: 'Сдвинуть вправо', Icon: ArrowRight },
  { value: 'up', label: 'Поднять выше', Icon: ArrowUp },
  { value: 'down', label: 'Опустить ниже', Icon: ArrowDown }
];

const WIDTH_STEPS = [
  { value: 'narrower', label: 'Уже', Icon: FoldHorizontal },
  { value: 'wider', label: 'Шире', Icon: UnfoldHorizontal }
];

const HEIGHT_STEPS = [
  { value: 'shorter', label: 'Ниже ростом', Icon: FoldVertical },
  { value: 'taller', label: 'Выше ростом', Icon: UnfoldVertical }
];

const STEP_BUTTON_CLASS =
  'flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-primary-foreground/15 '
  + 'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-foreground '
  + 'disabled:opacity-40 [&_svg]:size-3.5';

/** Высоту тянут только графики: у таблиц и шапок её задаёт содержимое. */
export const canResizeHeight = (kind) => kind === ITEM_KINDS.Fill || kind === ITEM_KINDS.Auto;

/**
 * Элемент сетки в режиме правки: ручка переноса, кнопки шагов для клавиатуры, уголок размера.
 *
 * Содержимое остаётся живым графиком с настоящими данными, но `inert`: клик по графику в
 * режиме правки не должен открывать журнал или менять фильтр, а фокус с клавиатуры — уходить
 * внутрь карточки мимо кнопок раскладки.
 */
const LayoutItemFrame = ({
  item,
  title,
  kind,
  cols,
  minWidth,
  children,
  onMoveStep,
  onResizeStep,
  onHide,
  dragHandlers,
  resizeHandlers
}) => {
  const heightResizable = canResizeHeight(kind);
  const ResizeIcon = heightResizable ? MoveDiagonal2 : MoveHorizontal;

  return (
    <div
      data-layout-item={item.id}
      className={cn(GRID_CELL_CLASS, 'relative rounded-xl outline-dashed outline-2 outline-offset-2 outline-primary/50')}
      style={cellStyle(item)}
    >
      <div className="absolute inset-x-0 top-0 z-10 flex items-center gap-0.5 rounded-t-xl bg-primary px-1.5 py-1 text-xs text-primary-foreground shadow-sm">
        <button
          type="button"
          data-ev="layout.item.drag_handle"
          aria-label={`Перетащить «${title}»`}
          title="Перетащить"
          className={cn(STEP_BUTTON_CLASS, 'cursor-grab touch-none active:cursor-grabbing')}
          {...dragHandlers}
        >
          <GripVertical />
        </button>
        <span className="min-w-0 flex-1 truncate font-medium" title={title}>{title}</span>
        <span className="shrink-0 px-1 tabular-nums opacity-80" title="Ширина в столбцах сетки">
          {item.w}/{cols}
        </span>
        {MOVE_STEPS.map(({ value, label, Icon }) => (
          <button
            key={value}
            type="button"
            data-ev="layout.item.move"
            data-ev-value={value}
            aria-label={`${label}: «${title}»`}
            title={label}
            className={STEP_BUTTON_CLASS}
            onClick={() => onMoveStep(value)}
          >
            <Icon />
          </button>
        ))}
        {WIDTH_STEPS.map(({ value, label, Icon }) => (
          <button
            key={value}
            type="button"
            data-ev="layout.item.resize"
            data-ev-value={value}
            aria-label={`${label}: «${title}»`}
            title={label}
            className={STEP_BUTTON_CLASS}
            disabled={value === 'narrower' ? item.w <= minWidth : item.w >= cols}
            onClick={() => onResizeStep(value)}
          >
            <Icon />
          </button>
        ))}
        {heightResizable && HEIGHT_STEPS.map(({ value, label, Icon }) => (
          <button
            key={value}
            type="button"
            data-ev="layout.item.resize"
            data-ev-value={value}
            aria-label={`${label}: «${title}»`}
            title={label}
            className={STEP_BUTTON_CLASS}
            disabled={value === 'shorter' && item.h <= 1}
            onClick={() => onResizeStep(value)}
          >
            <Icon />
          </button>
        ))}
        {onHide && (
          <button
            type="button"
            data-ev="layout.item.hide"
            aria-label={`Скрыть «${title}»`}
            title="Скрыть с вкладки"
            className={STEP_BUTTON_CLASS}
            onClick={onHide}
          >
            <EyeOff />
          </button>
        )}
      </div>

      <div inert className="flex min-h-0 min-w-0 grow flex-col [&>*]:min-w-0 [&>*]:grow">
        {children}
      </div>

      <button
        type="button"
        data-ev="layout.item.resize_handle"
        aria-label={`Изменить размер «${title}»`}
        title={heightResizable ? 'Тяните, чтобы изменить ширину и высоту' : 'Тяните, чтобы изменить ширину'}
        className={cn(
          'absolute bottom-0 right-0 z-10 flex h-5 w-5 touch-none items-center justify-center rounded-br-xl rounded-tl-md',
          'bg-primary text-primary-foreground [&_svg]:size-3',
          heightResizable ? 'cursor-nwse-resize' : 'cursor-ew-resize'
        )}
        {...resizeHandlers}
      >
        <ResizeIcon />
      </button>
    </div>
  );
};

export default LayoutItemFrame;
